import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import type { Profile } from '@/lib/supabase/database.types';
import type { UserRole } from '@/types/domain';
import { AuthContext } from './AuthContext';
import type { AuthContextValue, SignUpInput } from './types';

/**
 * Source de verite de l'identite dans l'application.
 *
 * Principe de securite : le ROLE affiche ici ne sert qu'a l'interface (afficher
 * ou masquer un menu). Il n'accorde aucun droit. Les droits reels sont appliques
 * par les policies RLS de PostgreSQL, qui relisent le role depuis la table
 * `profiles` a chaque requete. Un utilisateur qui modifierait son role dans le
 * navigateur ne gagnerait strictement rien.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const mounted = useRef(true);

  const loadProfile = useCallback(async (user: User | null) => {
    if (!supabase || !user) {
      setProfile(null);
      setProfileError(null);
      return;
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (!mounted.current) return;

    if (error) {
      // Tant que les migrations ne sont pas appliquées, la table n'existe pas.
      // On ne bloque pas l'application : on remonte l'information telle quelle.
      setProfile(null);
      setProfileError(error.message);
      return;
    }

    if (data) {
      setProfile(data);
      setProfileError(null);
      return;
    }

    // Session valide mais aucun profil : le compte a été créé avant que le
    // trigger existe. On le répare une fois pour toutes, côté serveur — sans
    // ce profil, l'utilisateur n'a aucun rôle et toutes les policies RLS le
    // refusent, avec un message incompréhensible pour lui.
    const { data: repaired, error: repairError } = await supabase.rpc('ensure_profile');

    if (!mounted.current) return;

    if (repairError) {
      setProfile(null);
      setProfileError(repairError.message);
      return;
    }

    setProfile((repaired as unknown as Profile) ?? null);
    setProfileError(repaired ? null : 'Aucun profil associé à ce compte.');
  }, []);

  useEffect(() => {
    mounted.current = true;

    if (!supabase) {
      setIsLoading(false);
      return () => {
        mounted.current = false;
      };
    }

    const client = supabase;

    void (async () => {
      const { data } = await client.auth.getSession();
      if (!mounted.current) return;
      setSession(data.session);
      await loadProfile(data.session?.user ?? null);
      if (mounted.current) setIsLoading(false);
    })();

    const { data: subscription } = client.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      void loadProfile(nextSession?.user ?? null);
    });

    return () => {
      mounted.current = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) throw new Error('Supabase n’est pas configuré.');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(translateAuthError(error.message));
  }, []);

  const signUp = useCallback(async (input: SignUpInput) => {
    if (!supabase) throw new Error('Supabase n’est pas configuré.');
    const { data, error } = await supabase.auth.signUp({
      email: input.email,
      password: input.password,
      options: {
        // Ces metadonnees sont lues par le trigger `handle_new_user` (Phase 2)
        // qui cree la ligne `profiles` correspondante cote serveur.
        data: {
          first_name: input.firstName,
          last_name: input.lastName,
          phone: input.phone,
          requested_role: input.role,
        },
      },
    });
    if (error) throw new Error(translateAuthError(error.message));
    return { needsEmailConfirmation: !data.session };
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setProfile(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    await loadProfile(session?.user ?? null);
  }, [loadProfile, session]);

  const value = useMemo<AuthContextValue>(() => {
    const role: UserRole | null = profile?.role ?? null;
    return {
      isLoading,
      session,
      user: session?.user ?? null,
      profile,
      role,
      isAuthenticated: Boolean(session),
      profileError,
      signIn,
      signUp,
      signOut,
      refreshProfile,
    };
  }, [isLoading, session, profile, profileError, signIn, signUp, signOut, refreshProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Messages Supabase (anglais) traduits pour l'interface. */
function translateAuthError(message: string): string {
  const map: Record<string, string> = {
    'Invalid login credentials': 'E-mail ou mot de passe incorrect.',
    'Email not confirmed': 'Votre adresse e-mail n’a pas encore été confirmée.',
    'User already registered': 'Un compte existe déjà avec cette adresse e-mail.',
    'Password should be at least 6 characters':
      'Le mot de passe doit contenir au moins 6 caractères.',
  };
  return map[message] ?? message;
}
